import { useState, useRef, useCallback } from 'react';
import { ASR_PROVIDERS } from '@/lib/audio/constants';
import { createLogger } from '@/lib/logger';
import { toast } from 'sonner';

const log = createLogger('AudioRecorder');

// TypeScript declarations for Web Speech API
declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Web Speech API not typed in lib.dom
    SpeechRecognition: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Web Speech API not typed in lib.dom
    webkitSpeechRecognition: any;
  }
}

export interface UseAudioRecorderOptions {
  onTranscription?: (text: string) => void;
  onError?: (error: string) => void;
}

export function useAudioRecorder(options: UseAudioRecorderOptions = {}) {
  const { onTranscription, onError } = options;

  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Web Speech API not typed
  const speechRecognitionRef = useRef<any>(null);
  // Synchronous lock to prevent rapid re-entry (React state updates are async)
  const busyRef = useRef(false);
  // 【2026-06-23 v5】forceServerASRRef — Web Speech API 失败后（同一次录音会话内），
  //   设 true → 下一次 startRecording() 强制走 MediaRecorder server ASR
  //   **不依赖任何持久化**（ref 一定有效，隐身浏览器 / 关闭浏览器后清空都不影响）
  //   简化 v4 实现：去掉 zustand persist + browserFallbackFlagRef + asrBrowserFallbackUsed 三重状态
  const forceServerASRRef = useRef(false);

  // Send audio to server for transcription
  const transcribeAudio = useCallback(
    async (audioBlob: Blob) => {
      setIsProcessing(true);

      try {
        const formData = new FormData();
        formData.append('audio', audioBlob, 'recording.webm');

        // Get current ASR configuration from settings store
        // Note: This requires importing useSettingsStore in browser context
        if (typeof window !== 'undefined') {
          const { useSettingsStore } = await import('@/lib/store/settings');
          const { asrProviderId, asrLanguage, asrProvidersConfig } = useSettingsStore.getState();

          formData.append('providerId', asrProviderId);
          formData.append(
            'modelId',
            asrProvidersConfig?.[asrProviderId]?.modelId ||
              ASR_PROVIDERS[asrProviderId as keyof typeof ASR_PROVIDERS]?.defaultModelId ||
              '',
          );
          formData.append('language', asrLanguage);

          // Append API key and base URL if configured
          const providerConfig = asrProvidersConfig?.[asrProviderId];
          if (providerConfig?.apiKey?.trim()) {
            formData.append('apiKey', providerConfig.apiKey);
          }
          const effectiveBaseUrl =
            providerConfig?.baseUrl?.trim() || providerConfig?.customDefaultBaseUrl || '';
          if (effectiveBaseUrl) {
            formData.append('baseUrl', effectiveBaseUrl);
          }
        }

        const response = await fetch('/api/transcription', {
          method: 'POST',
          body: formData,
        });

        // Read response body ONCE (response.json() consumes the body stream;
        // reading it twice throws "body stream already read").
        const body: { text?: string; error?: string; code?: string } = await response
          .json()
          .catch(() => ({}));

        // 503 BROWSER_FALLBACK_REQUIRED — server has no working ASR provider
        // (admin 没配 category=asr+is_default=1+status=1 的 provider，或 priority 链全失败)。
        // 抛特殊错误让 startRecording 路径下次走 Web Speech API。
        if (response.status === 503 && body?.code === 'BROWSER_FALLBACK_REQUIRED') {
          forceServerASRRef.current = false;
          throw new Error('SERVER_ASR_UNAVAILABLE');
        }

        if (!response.ok) {
          throw new Error(body.error || 'Transcription failed');
        }

        // 【2026-06-23 v5】空文本保护：Qwen ASR 返 text='' 通常表示录音太短/无语音
        //   （见 lib/audio/asr-providers.ts:298 'audio is empty' + :320 'Empty content'）
        //   老板报"录音结束后报未检测到语音，请重试"真因：之前没有空文本保护，
        //   speech-button 收到 '' 直接传空字符串给 chat-panel.setInput，没任何提示
        //   → 用户以为"什么都没识别出来"。修复：空文本 + 录音时长 < 2s → toast 友好提示
        const recognizedText = body.text || '';
        if (!recognizedText.trim()) {
          // 录音太短/无语音：友好提示（不弹红字，跟 v6.4 日志分级一致）
          toast.info('未检测到语音，请长按麦克风说话或重新录制');
          return;
        }
        onTranscription?.(recognizedText);
        // 成功：清 forceServerASRRef 让下次可以重新选择（如果配置改了）
        forceServerASRRef.current = false;
      } catch (error) {
        log.error('Transcription error:', error);
        // SERVER_ASR_UNAVAILABLE: 静默处理，让 startRecording 走 Web Speech 兜底
        if (error instanceof Error && error.message === 'SERVER_ASR_UNAVAILABLE') {
          log.warn('[useAudioRecorder] Server ASR unavailable, will use browser-native ASR on next recording.');
          return;
        }
        onError?.(error instanceof Error ? error.message : '语音识别失败，请重试');
      } finally {
        setIsProcessing(false);
        setRecordingTime(0);
      }
    },
    [onTranscription, onError],
  );

  // Start recording
  const startRecording = useCallback(async () => {
    // Synchronous lock — React state is async so isRecording may be stale
    if (busyRef.current) return;
    busyRef.current = true;
    try {
      // Get current ASR configuration
      if (typeof window !== 'undefined') {
        const { useSettingsStore } = await import('@/lib/store/settings');
        const { asrProviderId, asrLanguage } = useSettingsStore.getState();

        // 【2026-06-23 v5】useBrowserASR 简化逻辑（跟 OpenMAIC 官方对齐）
        //   官方原文：if (asrProviderId === 'browser-native') 走 Web Speech API
        //   v5 加 forceServerASRRef 短路：Web Speech 失败后强制下次走 server ASR
        //   **唯一增强** vs 官方：用 ref（不依赖任何持久化）实现 auto-fallback
        //   去掉 v4 复杂实现：zustand persist + browserFallbackFlagRef + asrBrowserFallbackUsed
        //   老板需求："录音结束后报未检测到语音，请重试" → 简化后 fallback 100% 可靠
        const useBrowserASR = asrProviderId === 'browser-native' && !forceServerASRRef.current;
        if (useBrowserASR) {
          // Check if Speech Recognition is supported
          if (!window.SpeechRecognition && !window.webkitSpeechRecognition) {
            onError?.('您的浏览器不支持语音识别功能');
            return;
          }

          const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
          const recognition = new SpeechRecognition();

          recognition.lang = asrLanguage || 'zh-CN';
          recognition.continuous = false;
          recognition.interimResults = false;

          recognition.onstart = () => {
            setIsRecording(true);
            setRecordingTime(0);

            // Start timer
            timerRef.current = setInterval(() => {
              setRecordingTime((prev) => prev + 1);
            }, 1000);
          };

          recognition.onresult = (event: {
            results: {
              [index: number]: { [index: number]: { transcript: string } };
            };
          }) => {
            const transcript = event.results[0][0].transcript;
            onTranscription?.(transcript);
          };

          recognition.onerror = (event: { error: string }) => {
            // 【2026-06-23 v5】log 分级（v6.4 修复保留）：不让 Web Speech 已知错写红字扰老板
            //   - 'network' / 'aborted' = 已知/正常的 fallback 触发器 → log.info
            //   - 'no-speech' / 'audio-capture' / 'not-allowed' = 用户侧问题 → log.warn
            //   - 其他 = 未知错误 → log.error
            const isBenign = event.error === 'network' || event.error === 'aborted';
            const isUserError = event.error === 'no-speech' || event.error === 'audio-capture' || event.error === 'not-allowed';
            if (isBenign) {
              log.info('Speech recognition:', event.error, '(will auto-fallback to server ASR)');
            } else if (isUserError) {
              log.warn('Speech recognition error:', event.error);
            } else {
              log.error('Speech recognition error:', event.error);
            }
            let errorMessage = '语音识别失败';

            switch (event.error) {
              case 'aborted':
                // Non-fatal: caused by our own cancel/stop logic or rapid toggle
                busyRef.current = false;
                setIsRecording(false);
                setRecordingTime(0);
                if (timerRef.current) {
                  clearInterval(timerRef.current);
                  timerRef.current = null;
                }
                return;
              case 'no-speech':
                // 【2026-06-23 v5】'no-speech' → auto-fallback server ASR（v4 简化版）
                //   老板真因：之前 'no-speech' 只弹错不 fallback → 永远卡死
                //   现在：forceServerASRRef=true + 释放 busyLock + 150ms 后递归调 startRecording
                //   → MediaRecorder 上传 /api/transcription → Qwen ASR 识别
                forceServerASRRef.current = true;
                errorMessage = '未检测到语音输入';
                busyRef.current = false;
                setIsRecording(false);
                setRecordingTime(0);
                if (timerRef.current) {
                  clearInterval(timerRef.current);
                  timerRef.current = null;
                }
                log.info('[useAudioRecorder] v5 auto-fallback: no-speech → 递归调 startRecording 强制走 server ASR');
                setTimeout(() => { startRecording(); }, 150);
                return;
              case 'audio-capture':
                errorMessage = '无法访问麦克风';
                break;
              case 'not-allowed':
                errorMessage = '麦克风权限被拒绝';
                break;
              case 'network':
                // 【2026-06-23 v5】'network' 同样 auto-fallback（老板浏览器没翻墙）
                forceServerASRRef.current = true;
                errorMessage = '网络错误';
                busyRef.current = false;
                setIsRecording(false);
                setRecordingTime(0);
                if (timerRef.current) {
                  clearInterval(timerRef.current);
                  timerRef.current = null;
                }
                log.info('[useAudioRecorder] v5 auto-fallback: network → 递归调 startRecording 强制走 server ASR');
                setTimeout(() => { startRecording(); }, 150);
                return;
              default:
                errorMessage = `语音识别错误: ${event.error}`;
            }

            onError?.(errorMessage);
            busyRef.current = false;
            setIsRecording(false);
            setRecordingTime(0);
            if (timerRef.current) {
              clearInterval(timerRef.current);
              timerRef.current = null;
            }
          };

          recognition.onend = () => {
            busyRef.current = false;
            setIsRecording(false);
            setRecordingTime(0);
            if (timerRef.current) {
              clearInterval(timerRef.current);
              timerRef.current = null;
            }
          };

          recognition.start();
          speechRecognitionRef.current = recognition;
          return;
        }
      }

      // Use MediaRecorder for server-side ASR
      // Request microphone permission
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Create MediaRecorder
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm',
      });

      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        // Stop all audio tracks
        stream.getTracks().forEach((track) => track.stop());

        // Merge audio chunks
        const audioBlob = new Blob(audioChunksRef.current, {
          type: 'audio/webm',
        });

        // Send to server for transcription
        await transcribeAudio(audioBlob);
        busyRef.current = false;
      };

      // Start recording
      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);

      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } catch (error) {
      busyRef.current = false;
      // 【2026-07-16 v6.97.41 P0 跟 OpenMAIC 官方对齐 + 老板 bug 报告修复】
      // 老板反馈: "点麦克风按钮提示:无法访问麦克风,请检查权限设置"
      //   之前 catch-all 统一文案把 Permission/设备/占用等场景混在一起,
      //   老板怀疑麦克风坏了,实际只是浏览器权限被拒绝 / Zoom 占用设备 等
      //   可快速自行解决的问题。
      // 修复: 跟 PromptInputSpeechButton 一致 — 按 error.name 分类给友好文案
      log.error('[useAudioRecorder] Failed to start recording:', error);
      const errorName = error instanceof Error ? error.name : '';
      let friendlyMsg: string;
      if (errorName === 'NotAllowedError' || errorName === 'SecurityError') {
        friendlyMsg = '无法访问麦克风:浏览器麦克风权限被拒绝。请点击地址栏的锁🔒图标 → 允许麦克风 → 刷新页面';
      } else if (errorName === 'NotFoundError' || errorName === 'OverconstrainedError') {
        friendlyMsg = '无法访问麦克风:未检测到麦克风设备。请插入麦克风后重试';
      } else if (errorName === 'NotReadableError') {
        friendlyMsg = '无法访问麦克风:设备被其他程序占用。请关闭 Zoom/腾讯会议等后重试';
      } else {
        friendlyMsg = '无法访问麦克风,请检查权限设置';
      }
      onError?.(friendlyMsg);
    }
  }, [onTranscription, onError, transcribeAudio]);

  // Stop recording
  const stopRecording = useCallback(() => {
    // Stop Speech Recognition if active
    if (speechRecognitionRef.current) {
      speechRecognitionRef.current.stop();
      speechRecognitionRef.current = null;
      busyRef.current = false;
      setIsRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    // Stop MediaRecorder if active
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      busyRef.current = false;
      setIsRecording(false);

      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  }, [isRecording]);

  // Cancel recording
  const cancelRecording = useCallback(() => {
    // Cancel Speech Recognition if active
    if (speechRecognitionRef.current) {
      speechRecognitionRef.current.onresult = null; // Prevent transcription callback
      speechRecognitionRef.current.onerror = null; // Suppress browser abort error events
      speechRecognitionRef.current.stop();
      speechRecognitionRef.current = null;
      busyRef.current = false;
      setIsRecording(false);
      setRecordingTime(0);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    // Cancel MediaRecorder if active
    if (mediaRecorderRef.current && isRecording) {
      // Stop recording without transcription
      mediaRecorderRef.current.ondataavailable = null;
      mediaRecorderRef.current.onstop = null;
      mediaRecorderRef.current.stop();

      // Stop all audio tracks
      if (mediaRecorderRef.current.stream) {
        mediaRecorderRef.current.stream.getTracks().forEach((track) => track.stop());
      }

      busyRef.current = false;
      setIsRecording(false);
      setRecordingTime(0);

      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }

      audioChunksRef.current = [];
    }
  }, [isRecording]);

  return {
    isRecording,
    isProcessing,
    recordingTime,
    startRecording,
    stopRecording,
    cancelRecording,
  };
}
